import { useCallback, useState } from "react";
import {
  CheckCircle2, ChevronDown, Clock, Loader2, MapPin, MessageSquare, Plane, UserX,
} from "lucide-react";
import { DRIVER_ACTIONS, clock, stageLabel } from "@/lib/lifecycle";
import { StageBadge } from "@/components/StageBadge";
import { NoShowButton } from "@/components/NoShowButton";
import { ChatPanel } from "@/components/ChatPanel";
import { DriverNavigate, NavigateButton } from "@/components/DriverNavigate";
import { currentPosition } from "@/lib/maps";
import { Button } from "@/components/ui/button";

// One colour per action so the chauffeur learns the button by sight, not by reading.
const ACTION_STYLE = {
  driver_accepted: "bg-blue-600 text-white hover:bg-blue-500",
  en_route_pickup: "bg-orange-500 text-ink-900 hover:bg-orange-400",
  arrived_pickup: "bg-purple-600 text-white hover:bg-purple-500",
  onboard: "bg-purple-500 text-white hover:bg-purple-400",
  en_route_destination: "bg-yellow-400 text-ink-900 hover:bg-yellow-300",
  arrived_destination: "bg-emerald-500 text-ink-900 hover:bg-emerald-400",
};

const Stop = ({ label, address, tone = "text-slate-100", icon }) => (
  <div className="flex gap-3">
    <span className="mt-1 shrink-0">{icon}</span>
    <span className="min-w-0">
      <span className="block font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <span className={`block text-base font-semibold leading-snug ${tone}`}>{address || "—"}</span>
    </span>
  </div>
);

export const DriverJobCard = ({ trip: t, tripState, nearPickup, busy, hero = false,
                                onAdvance, onDecline, onStopWait, onStopDecision, onReload, onError,
                                stopApprovalMode }) => {
  const [openChat, setOpenChat] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [from, setFrom] = useState(null);
  const stage = t.stage || (t.status === "declined" ? "declined" : "assigned");
  const action = DRIVER_ACTIONS[stage];
  const unread = t.unread_messages || 0;
  // Before the passenger is aboard the chauffeur is driving to the pickup; after that, to the drop.
  const target = ["onboard", "en_route_destination", "arrived_destination"].includes(stage)
    ? t.dropoff : t.pickup;

  const openNavigation = async () => {
    setNavOpen(true);
    try { setFrom(await currentPosition()); } catch { setFrom(null); }
  };
  const closeNavigation = useCallback(() => setNavOpen(false), []);

  return (
    <div data-testid={`driver-trip-${t.number}`}
      className={`rounded-2xl bg-ink-800 p-5 shadow-xl ${
        hero ? "border-2 border-gold/50" : "border border-ink-600"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-xs tracking-widest text-gold">TRIP #{t.number}</p>
          <p className="mt-1 text-xl font-bold leading-tight text-white">{t.customer_name}</p>
        </div>
        <span data-testid={`driver-trip-status-${t.number}`}>
          <StageBadge stage={stage} testId={`driver-stage-${t.number}`} />
        </span>
      </div>

      <div className="mt-4 flex items-center gap-2 rounded-xl bg-ink-900 px-4 py-3">
        <Clock className="h-4 w-4 shrink-0 text-gold" />
        <p className="text-base font-bold text-white">
          {t.pickup_time || "—"} <span className="font-normal text-slate-400">· {t.trip_date}</span>
        </p>
      </div>

      <div className="mt-4 space-y-3">
        <Stop label="Pickup" address={t.pickup?.address} icon={<MapPin className="h-4 w-4 text-gold" />} />
        {(t.stops || []).map((s, i) => (
          <div key={i} className="pl-7">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-sm ${s.status === "declined" ? "text-slate-600 line-through" : "text-slate-200"}`}>
                Stop {i + 1}: {s.address}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.status}</span>
            </div>
            {s.status !== "declined" && (
              <div className="mt-1.5 flex flex-wrap items-center gap-3">
                <button data-testid={`driver-stop-wait-${t.number}-${i}`} disabled={busy !== ""}
                  onClick={() => onStopWait(t, i, s.wait_started_at ? "stop" : "start")}
                  className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${
                    s.wait_started_at ? "border-amber-400/50 text-amber-300" : "border-ink-600 text-slate-300"}`}>
                  {s.wait_started_at ? "end wait" : "start wait"}
                </button>
                {s.wait_minutes > 0 && (
                  <span data-testid={`driver-stop-wait-total-${t.number}-${i}`} className="font-mono text-[10px] text-slate-400">
                    {s.wait_minutes} min held
                  </span>
                )}
                {s.status === "pending" && stopApprovalMode === "driver" && (
                  <>
                    <button data-testid={`driver-stop-approve-${t.number}-${i}`}
                      onClick={() => onStopDecision(t, i, "approve")} className="text-xs text-emerald-400 underline">accept</button>
                    <button data-testid={`driver-stop-decline-${t.number}-${i}`}
                      onClick={() => onStopDecision(t, i, "decline")} className="text-xs text-red-400 underline">decline</button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
        <Stop label="Drop-off" address={t.dropoff?.address} tone="text-slate-200"
          icon={<MapPin className="h-4 w-4 text-slate-500" />} />
        {(t.airline || t.flight_number) && (
          <Stop label="Flight" tone="text-slate-200"
            address={[t.airline, t.flight_number, t.terminal && `Terminal ${t.terminal}`].filter(Boolean).join(" · ")}
            icon={<Plane className="h-4 w-4 text-slate-500" />} />
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4 border-t border-ink-600 pt-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Vehicle</p>
          <p className="text-sm font-semibold text-slate-100">{t.vehicle_name || "—"}</p>
        </div>
        {t.driver_payout && (
          <div className="text-right">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Your payout</p>
            <p data-testid={`driver-payout-${t.number}`} className="font-display text-2xl font-extrabold text-emerald-300">
              ${Number(t.driver_payout.amount).toFixed(2)}
            </p>
          </div>
        )}
      </div>

      {t.special_instructions && (
        <p className="mt-4 rounded-xl border border-gold/25 bg-gold/5 p-3 text-sm leading-relaxed text-slate-200">
          {t.special_instructions}
        </p>
      )}

      {stage === "declined" && (
        <p data-testid={`driver-declined-${t.number}`} className="mt-5 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
          You declined this job. Dispatch has been told and can offer it to you again.
        </p>
      )}
      {stage === "no_show" && (
        <p data-testid={`driver-no-show-${t.number}`} className="mt-5 flex items-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 p-4 text-sm text-amber-200">
          <UserX className="h-4 w-4 shrink-0" /> Billed as a no-show. Dispatch can reverse it if the passenger disputes.
        </p>
      )}
      {["completed", "payment_finalized"].includes(stage) && (
        <p data-testid={`driver-trip-closed-${t.number}`} className="mt-5 flex items-center gap-2 text-sm text-slate-400">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Trip completed. Dispatch handles the billing.
        </p>
      )}

      {["assigned", "booked"].includes(stage) && (
        <div className="mt-5 space-y-3">
          <Button data-testid={`driver-accept-${t.number}`} disabled={busy !== ""}
            onClick={() => onAdvance(t, "driver_accepted")}
            className="h-16 w-full bg-emerald-500 text-lg font-extrabold tracking-wide text-ink-900 hover:bg-emerald-400 active:scale-[0.99]">
            {busy === `${t.id}-driver_accepted` ? <Loader2 className="h-5 w-5 animate-spin" /> : "ACCEPT JOB"}
          </Button>
          <Button data-testid={`driver-decline-${t.number}`} disabled={busy !== ""} variant="outline"
            onClick={() => onDecline(t)}
            className="h-12 w-full border-red-500/40 bg-transparent text-base text-red-300 hover:bg-red-500/10">
            {busy === `${t.id}-declined` ? <Loader2 className="h-4 w-4 animate-spin" /> : "Decline job"}
          </Button>
        </div>
      )}

      {stage === "driver_accepted" && (
        <p data-testid={`driver-accepted-banner-${t.number}`}
          className="mt-5 flex items-center gap-2 rounded-xl border border-emerald-500/50 bg-emerald-500/10 p-4 text-sm font-semibold text-emerald-200">
          <CheckCircle2 className="h-5 w-5 shrink-0" /> You accepted trip #{t.number} — dispatch can see it.
        </p>
      )}

      {!["assigned", "booked", "declined", "no_show", "completed", "payment_finalized"].includes(stage) && (
        <div className="mt-4 space-y-3">
          {nearPickup && stage === "en_route_pickup" && (
            <p data-testid={`driver-geofence-${t.number}`} className="rounded-xl border border-gold/40 bg-gold/10 p-3 text-sm font-semibold text-gold">
              You appear to be at the pickup location — confirm below.
            </p>
          )}
          {tripState?.trip_started_at && (
            <p data-testid={`driver-timer-${t.number}`} className="rounded-xl bg-ink-900 p-3 text-center font-mono text-sm text-slate-200">
              {clock(tripState.recorded_minutes)} on the road · {tripState.recorded_miles?.toFixed?.(1) ?? 0} mi
            </p>
          )}
          {action && (
            <Button data-testid={`driver-advance-${t.number}`} disabled={busy !== ""}
              onClick={() => onAdvance(t, action.stage)}
              className={`h-16 w-full text-lg font-extrabold tracking-wide active:scale-[0.99] ${
                ACTION_STYLE[stage] || "bg-gold text-ink-900 hover:bg-gold-light"}`}>
              {busy === `${t.id}-${action.stage}` ? <Loader2 className="h-5 w-5 animate-spin" /> : action.label}
            </Button>
          )}
          {stage === "arrived_pickup" && <NoShowButton trip={t} onDone={onReload} onError={onError} />}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        {target?.lat != null || target?.address ? (
          <NavigateButton testId={`driver-navigate-${t.number}`} onClick={openNavigation} />
        ) : <span />}
        <button data-testid={`driver-messages-toggle-${t.number}`} onClick={() => setOpenChat((v) => !v)}
          className="relative flex h-12 items-center justify-center gap-2 rounded-xl border border-ink-600 bg-ink-900 text-sm font-semibold text-slate-100">
          <MessageSquare className="h-4 w-4 text-gold" /> Messages
          {unread > 0 && !openChat && (
            <span data-testid={`driver-unread-${t.number}`}
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
              {unread}
            </span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openChat ? "rotate-180" : ""}`} />
        </button>
      </div>

      {openChat && (
        <div className="mt-3">
          <ChatPanel compact mine="driver"
            listUrl={`/driver/trips/${t.id}/messages`}
            sendUrl={`/driver/trips/${t.id}/messages`}
            title={`Message ${t.customer_name || "the passenger"}`} />
        </div>
      )}

      <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-widest text-slate-500">
        {stageLabel(stage)}
      </p>

      <DriverNavigate open={navOpen} onClose={closeNavigation} destination={target}
        from={from} label={target?.address} />
    </div>
  );
};
